import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from 'store/clients';
import { types, Venue, UpdateVenueRequest, CreateVenueRequest, DeleteVenueRequest } from './types';
import { API } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';

export function* getVenuesAsync() {
  yield put({ type: types.GET_VENUES.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/venues`);
    const response: Venue[] = yield call(api.getAll);

    yield put({ type: types.GET_VENUES.SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: types.GET_VENUES.ERROR, payload: 'Failed to get venues' });
  } finally {
    yield put({ type: types.GET_VENUES.PENDING, payload: false });
  }
}

export function* createVenueAsync({ payload }: CreateVenueRequest) {
  yield put({ type: types.CREATE_VENUE.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/venues`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_VENUE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_VENUE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_VENUE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_VENUE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_VENUE.PENDING, payload: false });
  }
}

export function* updateVenueAsync({ payload }: UpdateVenueRequest) {
  yield put({ type: types.UPDATE_VENUE.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/venues`);
    const response: Venue = yield call(api.update, payload);

    yield put({ type: types.UPDATE_VENUE.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;
    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_VENUE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_VENUE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_VENUE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_VENUE.PENDING, payload: false });
  }
}

export function* deleteVenueAsync({ payload }: DeleteVenueRequest) {
  yield put({ type: types.DELETE_VENUE.PENDING, payload: true });

  try {
    const { id } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/venues`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_VENUE.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_VENUE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_VENUE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_VENUE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_VENUE.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_VENUES.REQUEST, getVenuesAsync),
  takeLatest(types.CREATE_VENUE.REQUEST, createVenueAsync),
  takeLatest(types.UPDATE_VENUE.REQUEST, updateVenueAsync),
  takeLatest(types.DELETE_VENUE.REQUEST, deleteVenueAsync)
];
